import React, { useState, useRef, useEffect } from 'react';
import { debounce, Stack, IconButton } from '@mui/material';
import { SubNavigationV2 } from '@models/section/subNavigationSection';
import { subNavigationStyles as classes } from '@components/styleObjects/SubNavigationV2';
import { getLinkTargetValue } from '../SectionComponents/helpers/SectionComponentHelper';
import { PopoutButton } from '@components/PopoutCustomSection/PopoutButton';
import { Button, colors } from '@web-for-marketing/react-ui';
import { RightArrowIcon } from '@components/IconComponents/RightArrowIcon';

export interface V2SubNavigationProps {
    sectionData: SubNavigationV2;
}

export function V2SubNavigation({ sectionData }: V2SubNavigationProps): React.ReactElement {
    const containerRef = useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    useEffect(() => {
        const container = containerRef.current;

        if (!container) return;

        const resizeObserver = new ResizeObserver(() => {
            updateScrollArrows();
        });

        resizeObserver.observe(container);

        return () => {
            resizeObserver.unobserve(container);
        };
    }, []);

    const updateScrollArrows = (): void => {
        const container = containerRef.current;
        if (!container) return;

        setShowLeftArrow(container.scrollLeft > 0);
        setShowRightArrow(container.scrollLeft + container.clientWidth < container.scrollWidth);
    };

    const handleScroll = (direction: 'left' | 'right'): void => {
        const container = containerRef.current;
        const adjustment = direction === 'left' ? -120 : 120;
        if (container) {
            container.scrollTo({
                left: container.scrollLeft + adjustment, // Adjust scroll amount as needed
                behavior: 'smooth',
            });
            updateScrollArrows();
        }
    };

    const handleScrollDebounced = debounce(updateScrollArrows, 100);
    return (
        <div css={classes.subNavigationContainer}>
            <div css={classes.subNavigation}>
                {showLeftArrow ? (
                    <div css={classes.arrowContainer}>
                        <IconButton
                            onClick={() => handleScroll('left')}
                            css={classes.arrowButton}
                            aria-hidden
                            data-testid='scroll-left-button'
                            aria-label='Scroll left'
                            tabIndex={-1}
                        >
                            <RightArrowIcon color={colors.text.activePrimary} css={classes.flipArrow} />
                        </IconButton>
                    </div>
                ) : null}
                <Stack
                    width='auto'
                    gap='32px'
                    direction='row'
                    flexWrap='nowrap'
                    ref={containerRef}
                    data-testid='link-container'
                    css={classes.iconButtonLinkItemContainer}
                    onScroll={handleScrollDebounced}
                >
                    {sectionData.buttons.map((button, index) => {
                        return button.isPopout ? (
                            <PopoutButton
                                key={`${button.buttonText}-${index}`}
                                text={button.buttonText}
                                variant={button.buttonType || 'primary'}
                                customSectionKey={button.popoutCustomSection}
                                displayMode={button.popoutDisplayMode}
                                backgroundImagePath={button.popoutBackgroundImage}
                                color={button.buttonColor}
                                aria-label={button.ariaLabel}
                                params={button.params}
                                isV2
                            />
                        ) : (
                            <Button
                                component='a'
                                variant={button.buttonType || 'primary'}
                                key={`${index}${button.buttonText}`}
                                color={button.buttonColor}
                                href={button.buttonLinkPath}
                                target={getLinkTargetValue(button.buttonTarget)}
                                aria-label={button.ariaLabel}
                            >
                                {button.buttonText}
                            </Button>
                        );
                    })}
                </Stack>
                {showRightArrow ? (
                    <div css={classes.arrowContainer}>
                        <IconButton
                            onClick={() => handleScroll('right')}
                            css={classes.arrowButton}
                            size='large'
                            aria-hidden
                            tabIndex={-1}
                            data-testid='scroll-right-button'
                            aria-label='Scroll right'
                        >
                            <RightArrowIcon color={colors.text.activePrimary} />
                        </IconButton>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
